'use client';

import { useCallback, useState } from 'react';
import {
  ExternalLink,
  BadgeCheck,
  Globe,
  TrendingUp,
  TrendingDown,
  Users,
  Zap,
  Eye,
  ShieldCheck,
  ShieldX,
  ShieldAlert,
} from 'lucide-react';
import Image from 'next/image';
import type { TokenResult } from '@/lib/web3/types';
import { looksLikeMint, type TokenSuggestion } from '@/lib/web3/token-search';
import ResultPanel from '@/components/web3/ResultPanel';
import TokenSearchField from '@/components/web3/TokenSearchField';
import TokenRadar from '@/components/web3/TokenRadar';
import TimeframeGrid from '@/components/web3/TimeframeGrid';

/* ───────────────────── helpers ───────────────────── */

const VERDICT_LABEL: Record<TokenResult['verdict'], string> = {
  safe: 'Active market',
  caution: 'Trade with caution',
  danger: 'High risk',
};

function fmtUsd(n?: number | null): string {
  if (n == null || !Number.isFinite(n)) return '-';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
  if (n > 0 && n < 0.01) return `$${n.toPrecision(2)}`;
  return `$${n.toFixed(2)}`;
}

function fmtPct(n: number | null): string {
  if (n == null) return '-';
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
}

function fmtNum(n: number | null): string {
  if (n == null) return '-';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return `${n}`;
}

function PctTag({ val, size = 'sm' }: { val: number | null; size?: 'sm' | 'lg' }) {
  if (val == null) return <span className="text-neutral-600">-</span>;
  const pos = val >= 0;
  const cls = size === 'lg' ? 'text-base font-semibold' : 'text-xs font-mono';
  return (
    <span className={`inline-flex items-center gap-0.5 ${cls} ${pos ? 'text-emerald-400' : 'text-rose-400'}`}>
      {pos ? <TrendingUp className="w-3 h-3 shrink-0" /> : <TrendingDown className="w-3 h-3 shrink-0" />}
      {fmtPct(val)}
    </span>
  );
}

function Metric({ label, value, sub, warn }: { label: string; value: string; sub?: string; warn?: boolean }) {
  return (
    <div className={`p-3 border ${warn ? 'border-rose-500/30 bg-rose-500/5' : 'border-white/8 bg-black/30'}`}>
      <p className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</p>
      <p className={`mt-1 text-sm font-mono tabular-nums ${warn ? 'text-rose-300' : 'text-white'}`}>{value}</p>
      {sub && <p className="mt-0.5 text-[10px] text-neutral-600">{sub}</p>}
    </div>
  );
}

function AuthBadge({ label, renounced }: { label: string; renounced: boolean }) {
  return (
    <span className={`inline-flex items-center gap-1.5 border px-2.5 py-1 text-xs ${
      renounced
        ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
        : 'border-rose-500/40 bg-rose-500/10 text-rose-300'
    }`}>
      <span className={`h-1.5 w-1.5 rounded-full ${renounced ? 'bg-emerald-400' : 'bg-rose-400'}`} />
      {label}: {renounced ? 'renounced' : 'active'}
    </span>
  );
}

function PressureBar({ buyVol, sellVol }: { buyVol: number | null; sellVol: number | null }) {
  const b = buyVol ?? 0;
  const s = sellVol ?? 0;
  const total = b + s;
  if (total === 0) return null;
  const buyPct = Math.round((b / total) * 100);
  const bullish = buyPct >= 50;
  return (
    <div className="border border-white/8 bg-black/30 p-4">
      <p className="text-[10px] uppercase tracking-wider text-neutral-500 mb-2">24h buy / sell pressure</p>
      <div className="flex h-2 w-full overflow-hidden">
        <div className="h-full bg-emerald-500/70" style={{ width: `${buyPct}%` }} />
        <div className="h-full bg-rose-500/50 flex-1" />
      </div>
      <div className="mt-1.5 flex justify-between text-[10px]">
        <span className="text-emerald-400/80 font-mono">{buyPct}% buy · {fmtUsd(b)}</span>
        <span className={`font-semibold ${bullish ? 'text-emerald-400' : 'text-rose-400'}`}>
          {bullish ? 'Buying pressure ↑' : 'Selling pressure ↓'}
        </span>
        <span className="text-rose-400/80 font-mono">{100 - buyPct}% sell · {fmtUsd(s)}</span>
      </div>
    </div>
  );
}

/* ───────────────────── Score hero ───────────────────── */

function ScoreHero({ result }: { result: TokenResult }) {
  const safety = 100 - result.score;
  const intel = result.intel;
  const m = result.market;

  const Icon = result.verdict === 'safe' ? ShieldCheck : result.verdict === 'caution' ? ShieldAlert : ShieldX;
  const color = result.verdict === 'safe' ? '#34d399' : result.verdict === 'caution' ? '#fbbf24' : '#fb7185';
  const ring = result.verdict === 'safe' ? 'border-emerald-500/40 bg-emerald-500/5'
    : result.verdict === 'caution' ? 'border-amber-500/40 bg-amber-500/5'
    : 'border-rose-500/40 bg-rose-500/5';

  return (
    <div className={`border ${ring} p-5`}>
      <div className="flex items-start gap-4">
        {/* Icon + identity */}
        <div className="flex flex-col items-center gap-1 shrink-0">
          {intel?.icon ? (
            <div className="relative">
              <Image src={intel.icon} alt="" width={48} height={48} className="rounded-full" unoptimized />
              <Icon className="absolute -bottom-1 -right-1 w-5 h-5 p-0.5 bg-black rounded-full" style={{ color }} />
            </div>
          ) : (
            <Icon className="w-10 h-10" style={{ color }} />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xl font-bold text-white">
              {m?.symbol ?? '—'}
            </span>
            {m?.name && m.name !== m.symbol && (
              <span className="text-sm text-neutral-500">{m.name}</span>
            )}
            {intel?.verified && (
              <BadgeCheck className="w-4 h-4 text-emerald-400 shrink-0" />
            )}
          </div>
          <p className="text-sm mt-0.5" style={{ color }}>
            {VERDICT_LABEL[result.verdict]}
          </p>
          {result.mint && (
            <p className="mt-1 text-[10px] font-mono text-neutral-600 break-all">
              {result.mint}
            </p>
          )}
        </div>

        {/* Safety score */}
        <div className="shrink-0 text-right">
          <p className="text-4xl font-bold tabular-nums font-mono" style={{ color }}>{safety}</p>
          <p className="text-[10px] uppercase tracking-wider text-neutral-500">safety/100</p>
        </div>
      </div>

      {/* Safety bar */}
      <div className="mt-4 space-y-1">
        <div className="flex justify-between text-[9px] text-neutral-600 uppercase tracking-wider">
          <span>Dangerous</span>
          <span>Safe</span>
        </div>
        <div className="h-2 w-full bg-black/50 overflow-hidden">
          <div
            className="h-full transition-all duration-700"
            style={{ width: `${safety}%`, backgroundColor: color }}
          />
        </div>
      </div>
    </div>
  );
}

/* ───────────────────── Main component ───────────────────── */

export default function TokenCheck() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<TokenResult | null>(null);

  const run = useCallback(async (target: string) => {
    let value = target.trim();
    if (!value) {
      setError('Enter a token symbol or paste a mint address.');
      return;
    }

    if (!looksLikeMint(value)) {
      try {
        const res = await fetch(`/api/token-search?q=${encodeURIComponent(value)}`);
        const data = (await res.json()) as { results?: TokenSuggestion[] };
        const list = data.results ?? [];
        const exact = list.find((t) => t.symbol.toLowerCase() === value.toLowerCase());
        const picked = exact ?? list[0];
        if (!picked) {
          setError('No token found. Try another symbol or paste the full mint address.');
          return;
        }
        value = picked.mint;
      } catch {
        setError('Could not look up that symbol. Paste the full mint address instead.');
        return;
      }
    }

    setError('');
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch('/api/token-check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mint: value }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error || 'Check failed. Try again.');
        return;
      }
      setResult(data as TokenResult);
    } catch {
      setError('Network error. Try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const m = result?.market;
  const intel = result?.intel;

  return (
    <div className="w-full">
      {/* ── Search bar ── */}
      <div className="border border-white/10 bg-neutral-900/50 p-5 sm:p-6">
        <TokenSearchField loading={loading} error={error} onCheck={run} />
      </div>

      {result && (
        /* ── 2-column grid on lg+ ── */
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">

          {/* ══ LEFT COLUMN ══ */}
          <div className="flex flex-col gap-5">
            {/* Radar */}
            <TokenRadar result={result} />

            {/* Timeframe grid */}
            {intel?.available && <TimeframeGrid intel={intel} />}
          </div>

          {/* ══ RIGHT COLUMN ══ */}
          <div className="flex flex-col gap-4">
            {/* Score hero */}
            <ScoreHero result={result} />

            {/* Market metrics */}
            {m?.found && (
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-neutral-600 mb-2">Market</p>
                <div className="grid grid-cols-2 gap-2">
                  <Metric label="Price" value={fmtUsd(m.priceUsd)} />
                  <Metric
                    label="Market cap"
                    value={fmtUsd(m.marketCap ?? m.fdv)}
                    sub={m.marketCap ? 'mcap' : 'fdv'}
                  />
                  <Metric
                    label="Liquidity"
                    value={fmtUsd(m.liquidityUsd)}
                    sub={intel?.liquidityChange24h != null ? `${fmtPct(intel.liquidityChange24h)} 24h` : undefined}
                  />
                  <Metric label="Vol 24h" value={fmtUsd(m.volume24h)} />
                  <Metric
                    label="Age"
                    value={m.ageHours != null ? (m.ageHours < 24 ? `${m.ageHours}h` : `${Math.round(m.ageHours / 24)}d`) : '-'}
                  />
                  {intel?.holderCount != null && (
                    <Metric
                      label="Holders"
                      value={intel.holderCount.toLocaleString()}
                      sub={intel.holderChange24h != null ? `${fmtPct(intel.holderChange24h)} 24h` : undefined}
                    />
                  )}
                  {intel?.numTraders24h != null && (
                    <Metric label="Traders 24h" value={fmtNum(intel.numTraders24h)} />
                  )}
                  {intel?.numNetBuyers24h != null && (
                    <Metric
                      label="Net buyers 24h"
                      value={intel.numNetBuyers24h > 0 ? `+${intel.numNetBuyers24h}` : `${intel.numNetBuyers24h}`}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Buy / sell pressure */}
            {intel && <PressureBar buyVol={intel.buyVolume24h} sellVol={intel.sellVolume24h} />}

            {/* Jupiter deep intel */}
            {intel?.available && (
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-neutral-600 mb-2">Intelligence (Jupiter)</p>
                <div className="border border-white/8 bg-black/30 p-4 space-y-4">

                  {/* Organic score */}
                  {intel.organicScore != null && (
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs text-neutral-400 flex items-center gap-1.5">
                          <Zap className="w-3.5 h-3.5 text-amber-400" />
                          Organic activity
                        </span>
                        <span className={`text-xs font-semibold ${
                          intel.organicScoreLabel === 'high' ? 'text-emerald-400'
                          : intel.organicScoreLabel === 'medium' ? 'text-amber-400'
                          : 'text-rose-400'
                        }`}>
                          {intel.organicScore}/100 · {intel.organicScoreLabel}
                        </span>
                      </div>
                      <div className="h-1.5 w-full bg-black/50 overflow-hidden">
                        <div
                          className={`h-full transition-all duration-700 ${
                            intel.organicScoreLabel === 'high' ? 'bg-emerald-500'
                            : intel.organicScoreLabel === 'medium' ? 'bg-amber-400'
                            : 'bg-rose-500'
                          }`}
                          style={{ width: `${intel.organicScore}%` }}
                        />
                      </div>
                      <p className="mt-1 text-[10px] text-neutral-600">Real users vs bots. Low = suspicious.</p>
                    </div>
                  )}

                  {/* Organic buy ratio */}
                  {intel.organicBuyRatio != null && (
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs text-neutral-400 flex items-center gap-1.5">
                          <Eye className="w-3.5 h-3.5 text-neutral-400" />
                          Real buys (24h)
                        </span>
                        <span className="text-xs font-mono text-neutral-300">
                          {Math.round(intel.organicBuyRatio * 100)}% organic
                        </span>
                      </div>
                      <div className="h-1.5 w-full bg-black/50 overflow-hidden">
                        <div className="h-full bg-sky-500/70 transition-all duration-700"
                          style={{ width: `${Math.round(intel.organicBuyRatio * 100)}%` }} />
                      </div>
                      <p className="mt-1 text-[10px] text-neutral-600">% of buy volume from real wallets. &lt;20% is a red flag.</p>
                    </div>
                  )}

                  {/* Concentration */}
                  <div className="grid grid-cols-2 gap-2">
                    {intel.devBalancePct != null && (
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-neutral-500">Dev wallet</p>
                        <p className={`mt-0.5 text-sm font-mono font-semibold ${intel.devBalancePct >= 10 ? 'text-rose-300' : 'text-neutral-200'}`}>
                          {intel.devBalancePct.toFixed(2)}%
                        </p>
                        <p className="text-[10px] text-neutral-600">of supply</p>
                      </div>
                    )}
                    {intel.topHoldersPct != null && (
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-neutral-500">Top holders</p>
                        <p className={`mt-0.5 text-sm font-mono font-semibold ${intel.topHoldersPct >= 60 ? 'text-rose-300' : intel.topHoldersPct >= 35 ? 'text-amber-300' : 'text-neutral-200'}`}>
                          {intel.topHoldersPct.toFixed(1)}%
                        </p>
                        <p className="text-[10px] text-neutral-600">concentration</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* On-chain security */}
            {result.onchain?.available && (
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-neutral-600 mb-2 flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5" /> On-chain security
                </p>
                <div className="flex flex-wrap gap-2">
                  <AuthBadge label="Mint authority" renounced={!result.onchain.mintAuthority} />
                  <AuthBadge label="Freeze authority" renounced={!result.onchain.freezeAuthority} />
                  {result.onchain.topHolderShare !== null && (
                    <span className="inline-flex items-center gap-1.5 border border-white/10 bg-black/30 px-2.5 py-1 text-xs text-neutral-300">
                      Top holder ~{Math.round(result.onchain.topHolderShare * 100)}%
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Price change snapshot */}
            {intel?.priceChange24h != null && (
              <div className="flex gap-3">
                {intel.priceChange1h != null && (
                  <div className="flex-1 border border-white/8 bg-black/30 p-3 text-center">
                    <p className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">1H</p>
                    <PctTag val={intel.priceChange1h} size="lg" />
                  </div>
                )}
                {intel.priceChange6h != null && (
                  <div className="flex-1 border border-white/8 bg-black/30 p-3 text-center">
                    <p className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">6H</p>
                    <PctTag val={intel.priceChange6h} size="lg" />
                  </div>
                )}
                <div className="flex-1 border border-white/8 bg-black/30 p-3 text-center">
                  <p className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">24H</p>
                  <PctTag val={intel.priceChange24h} size="lg" />
                </div>
              </div>
            )}

            {/* Detection signals */}
            <ResultPanel
              verdict={result.verdict}
              score={result.score}
              label={`${VERDICT_LABEL[result.verdict]}${m?.symbol ? ` — ${m.symbol}` : ''}`}
              subtitle={null}
              signals={result.signals}
              footer={
                <div className="mt-4 flex flex-wrap gap-2">
                  {m?.url && (
                    <a href={m.url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 h-9 px-4 border border-white/15 text-xs text-white hover:border-white/40 transition-colors">
                      Chart <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                  {result.mint && (
                    <a href={`https://jup.ag/tokens/${result.mint}`} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 h-9 px-4 border border-white/15 text-xs text-white hover:border-white/40 transition-colors">
                      Jupiter <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                  {intel?.twitter && (
                    <a href={intel.twitter} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 h-9 px-4 border border-white/15 text-xs text-white hover:border-white/40 transition-colors">
                      <Users className="w-3.5 h-3.5" /> X
                    </a>
                  )}
                  {intel?.website && (
                    <a href={intel.website} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 h-9 px-4 border border-white/15 text-xs text-white hover:border-white/40 transition-colors">
                      <Globe className="w-3.5 h-3.5" /> Website
                    </a>
                  )}
                </div>
              }
            >
              <></>
            </ResultPanel>

            <p className="text-[10px] text-neutral-700">
              Sources: Dexscreener · Jupiter Token API · Solana RPC. Heuristic snapshot — not financial advice. DYOR.
            </p>
          </div>
          {/* ══ END RIGHT COLUMN ══ */}
        </div>
      )}
    </div>
  );
}
